/**
 * The overflow menu.
 *
 * A row of chrome has a width budget, and the terminal pane's is the tightest in
 * the console: four labelled buttons beside an identity left the session's own
 * name two characters wide in a grid of three. This is where the actions that
 * are not the primary one go, so the row can spend its width on saying which
 * session it is.
 *
 * Built on Headless UI for the same reason the Dialog is: the focus management,
 * the Escape handling, the roving `role="menuitem"` keyboard, the return of
 * focus to the trigger on close and the portal that escapes an `overflow:
 * hidden` ancestor are all details that rot when hand-rolled. The pane clips its
 * own overflow to round its corners and keep the canvas inside its border, so an
 * absolutely-positioned popover drawn inside it is cut off at the header — the
 * portal is not a nicety here, it is the only thing that works.
 */
import type { ReactNode } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { MoreHorizontal } from 'lucide-react'
import clsx from 'clsx'

import { Icon } from './icon'
import './menu.css'

export interface OverflowMenuProps {
  /** Accessible name for the trigger, which carries no visible text. */
  label: string
  children: ReactNode
  /** Matches the trigger to the buttons it sits beside. */
  size?: 'sm' | 'md'
}

export function OverflowMenu({ label, children, size = 'sm' }: OverflowMenuProps) {
  return (
    <Menu>
      <MenuButton
        className={clsx('k-btn', 'k-btn--ghost', `k-btn--${size}`, 'k-menu__trigger')}
        aria-label={label}
        title={label}
      >
        <Icon icon={MoreHorizontal} size={16} />
      </MenuButton>
      {/* Anchored rather than absolutely positioned: `anchor` renders the panel
          in a portal and keeps it against the trigger, which is what lets it
          hang outside the pane and flip up when the pane sits at the bottom of
          the viewport. */}
      <MenuItems anchor={{ to: 'bottom end', gap: 4 }} className="k-menu" modal={false}>
        {children}
      </MenuItems>
    </Menu>
  )
}

export interface OverflowItemProps {
  onClick: () => void
  children: ReactNode
  /** Shown dimmed at the trailing edge — a keyboard shortcut, or a hint. */
  hint?: string
  /** Disabled rather than hidden, with the reason in `title`: a control that
   *  vanishes while an agent works teaches an operator that it is unreliable,
   *  and they have no way to learn when it returns. */
  disabled?: boolean
  title?: string
}

/**
 * One command.
 *
 * Deliberately a command and not a toggle. Headless UI's Menu sets
 * `role="menuitem"` on whatever it renders and overrides anything passed in —
 * its keyboard walker and its release handler both select on that exact role —
 * so a `menuitemcheckbox` here would be a claim the markup does not keep, which
 * is worse than not making it. An entry whose state matters says so in its
 * label: "enter copy mode" and "leave copy mode" are two honest commands, where
 * one entry with an invisible checked state is a promise to a screen reader
 * that nothing fulfils.
 */
export function OverflowItem({ onClick, children, hint, disabled, title }: OverflowItemProps) {
  return (
    <MenuItem>
      <button type="button" className="k-menu__item" onClick={onClick} disabled={disabled} title={title}>
        <span className="k-menu__label">{children}</span>
        {hint && <span className="k-menu__hint k-mono">{hint}</span>}
      </button>
    </MenuItem>
  )
}
